import { ERROR_DEFINITIONS, type ErrorCode, type FieldError } from '@lsc/shared';

/** Client-only codes for failures that never reached the API. */
export type ClientErrorCode = 'NETWORK_ERROR' | 'INVALID_RESPONSE';

const CLIENT_MESSAGES: Record<ClientErrorCode, string> = {
  NETWORK_ERROR: 'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.',
  INVALID_RESPONSE: ERROR_DEFINITIONS.INTERNAL_ERROR.message,
};

/** Uniform error thrown by the API client (§96). `requestId` is shown as „Fehler-ID“ (§66). */
export class ApiError extends Error {
  readonly code: ErrorCode | ClientErrorCode;
  readonly status: number;
  readonly requestId: string | null;
  readonly fieldErrors: readonly FieldError[];
  readonly retryAfterSeconds: number | null;

  constructor(init: {
    code: ErrorCode | ClientErrorCode;
    status: number;
    message?: string;
    requestId: string | null;
    fieldErrors?: readonly FieldError[];
    retryAfterSeconds?: number | null;
  }) {
    super(init.message ?? ApiError.defaultMessage(init.code));
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.fieldErrors = init.fieldErrors ?? [];
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
  }

  static defaultMessage(code: ErrorCode | ClientErrorCode): string {
    return code in CLIENT_MESSAGES
      ? CLIENT_MESSAGES[code as ClientErrorCode]
      : ERROR_DEFINITIONS[code as ErrorCode].message;
  }

  /** Error message for one form field, if the server reported one. */
  fieldError(path: string): string | undefined {
    return this.fieldErrors.find((f) => f.path === path)?.message;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
